import React, { useState, useEffect, useRef, useCallback } from "react";
import api from "../../services/api";
import NotificationsDropdown from "./NotificationsDropdown";
import { useAuth } from "../../hooks/useAuth.js";
import { useActiveLeague } from "../../hooks/useActiveLeague";
import { useSocket } from "../../context/SocketContext.jsx";
import TutorialTooltip from './TutorialTooltip';

function getNotificationLeagueSlug(notification) {
    return notification?.leagueSlug || notification?.data?.leagueSlug || null;
}

function notificationMatchesActiveLeague(notification, activeLeagueSlug) {
    const notificationLeagueSlug = getNotificationLeagueSlug(notification);
    return activeLeagueSlug ? notificationLeagueSlug === activeLeagueSlug : true;
}

const NotificationBadge = ({ currentTeamId }) => {
    const [unreadCount, setUnreadCount] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const [countsLeagueSlug, setCountsLeagueSlug] = useState(null);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [filter, setFilter] = useState('unread'); // 'all' or 'unread'
    const [liveRefreshSignal, setLiveRefreshSignal] = useState(0);

    const { session } = useAuth();
    const isAuth = !!session;
    const { activeLeague } = useActiveLeague();
    const socketCtx = useSocket();
    const activeLeagueSlug = activeLeague?.slug;
    const activeLeagueSlugRef = useRef(activeLeagueSlug);
    const countsLeagueSlugRef = useRef(countsLeagueSlug);
    const liveNotificationIdsRef = useRef(new Set());
    const countRequestSeqRef = useRef(0);

    useEffect(() => {
        activeLeagueSlugRef.current = activeLeagueSlug;
    }, [activeLeagueSlug]);

    useEffect(() => {
        countsLeagueSlugRef.current = countsLeagueSlug;
    }, [countsLeagueSlug]);

    const fetchCounts = useCallback(async () => {
        if (!isAuth) return;
        const leagueSlugForRequest = activeLeagueSlug;
        const requestSeq = countRequestSeqRef.current + 1;
        countRequestSeqRef.current = requestSeq;
        console.log('Fetching notification counts for league:', activeLeagueSlug);
        setIsLoading(true);
        const currentFilter = { leagueSlug: activeLeagueSlug }; // Filter by active league
        try {
            const [unreadData, totalData] = await Promise.all([
                api.getUnreadCount(currentFilter),
                api.getTotalCount(currentFilter)
            ]);

            // Ignore stale responses so an older request cannot overwrite a
            // newer count refresh or a newer active league.
            if (
                requestSeq !== countRequestSeqRef.current ||
                leagueSlugForRequest !== activeLeagueSlugRef.current
            ) {
                return;
            }

            // Update unread count
            if (unreadData && typeof unreadData.count === 'number') {
                console.log(`Setting unread count to ${unreadData.count}`);
                setUnreadCount(unreadData.count);
            } else {
                console.warn('Unable to get unread count, using 0');
                setUnreadCount(0); // Set to 0 if fetch failed or data is invalid
            }

            // Update total count
            if (totalData && typeof totalData.count === 'number') {
                console.log(`Setting total count to ${totalData.count}`);
                setTotalCount(totalData.count);
            } else {
                console.warn('Unable to get total count, using 0');
                setTotalCount(0); // Set to 0 if fetch failed or data is invalid
            }

            // If at least one request succeeded, clear the error state
            setHasError(false);
            setCountsLeagueSlug(leagueSlugForRequest ?? null);
        } catch (error) {
            if (
                requestSeq !== countRequestSeqRef.current ||
                leagueSlugForRequest !== activeLeagueSlugRef.current
            ) {
                return;
            }

            // Log detailed error information if *both* fetches ultimately failed
            console.error('Error fetching notification counts (both failed):', error);
            console.error('Error details:', {
                message: error.message,
                status: error.response?.status,
                statusText: error.response?.statusText,
                data: error.response?.data
            });

            // Handle authentication errors
            if (error.response?.status === 401) {
                console.log('Authentication error, redirecting to login');
                localStorage.removeItem('token'); // Clear invalid token
                localStorage.removeItem('user'); // Clear user data
                // The axios interceptor will handle the redirect
                return; // Prevent further state updates
            }

            // Set error state true - this happens only if both requests fail
            setHasError(true);

            // If counts are for a different league, reset to avoid showing stale data
            if (countsLeagueSlugRef.current !== activeLeagueSlugRef.current) {
                setUnreadCount(0);
                setTotalCount(0);
            }
        } finally {
            if (requestSeq === countRequestSeqRef.current) {
                setIsLoading(false);
            }
        }
    }, [activeLeagueSlug, isAuth]);

    useEffect(() => {
        console.log('NotificationBadge useEffect triggered. isAuth:', isAuth, 'activeLeague:', activeLeagueSlug);

        if (isAuth) {
            console.log('User is authenticated, fetching initial counts.');
            setUnreadCount(0);
            setTotalCount(0);
            setCountsLeagueSlug(null);
            setHasError(false);
            fetchCounts(); // Fetch counts on mount or when auth/league changes
        } else {
            console.log('User not authenticated, resetting counts.');
            countRequestSeqRef.current += 1;
            setUnreadCount(0);
            setTotalCount(0);
            setCountsLeagueSlug(null);
            setHasError(false);
            setIsLoading(false);
        }

        return () => {
            console.log('NotificationBadge cleanup finished.');
        };
    }, [activeLeagueSlug, fetchCounts, isAuth]); // Refetch if auth status or league changes

    useEffect(() => {
        if (!isAuth || !socketCtx?.subscribe) {
            return undefined;
        }

        return socketCtx.subscribe('notification:new', (notification) => {
            if (!notificationMatchesActiveLeague(notification, activeLeagueSlugRef.current)) {
                return;
            }

            const notificationId = notification?._id || notification?.id;
            if (notificationId) {
                const normalizedId = String(notificationId);
                if (liveNotificationIdsRef.current.has(normalizedId)) {
                    return;
                }
                liveNotificationIdsRef.current.add(normalizedId);
            }

            setHasError(false);
            setCountsLeagueSlug(activeLeagueSlugRef.current ?? null);
            setTotalCount((count) => count + 1);
            if (notification?.read !== true) {
                setUnreadCount((count) => count + 1);
            }
            setLiveRefreshSignal((value) => value + 1);
            fetchCounts();
        });
    }, [fetchCounts, isAuth, socketCtx]);

    // Update counters when dropdown is closed
    const handleCloseDropdown = () => {
        console.log('Closing notification dropdown');
        setIsDropdownOpen(false);
        fetchCounts(); // Fetch counts immediately on close
    };

    // Handle filter change
    const handleFilterChange = (newFilter) => {
        console.log('Changing notification filter to:', newFilter);
        setFilter(newFilter);
        // Note: Consider if fetching should happen immediately on filter change
        // fetchCounts(); // Uncomment if needed
    };

    // Handle click on notification icon
    const handleNotificationClick = (event) => {
        console.log('Notification icon clicked');
        event.stopPropagation();
        // Toggle dropdown using functional updater to ensure latest state
        setIsDropdownOpen(prev => {
            if (!prev) {
                // We are about to open the dropdown, refresh counts first
                fetchCounts();
            }
            return !prev;
        });
    };

    console.log('Rendering NotificationBadge, unreadCount:', unreadCount, 'isDropdownOpen:', isDropdownOpen);

    if (!isAuth) {
        return null;
    }

    // Compute boolean for readability
    const hasUnread = unreadCount > 0 && !hasError;

    return (
        <div className="notifications-col">
            <TutorialTooltip
                id="notifications-tooltip"
                title="Notifications"
                description="Click on the Notifications icon to see all of your notifications, This is where you will receive transfer offers for your players,"
                className="f-top"
            />
            <button 
                type="button"
                className={`ctrl-btn icon-notif notifications_btn ${hasError ? 'notification-error' : ''} ${hasUnread ? 'has-unread' : ''}`}
                onClick={handleNotificationClick}
                onMouseDown={(e) => e.stopPropagation()} // Prevent outside-click handler from firing
                title={hasError ? 'Could not load notifications' : 'Notifications'}
                aria-label={hasUnread ? 'Notifications (new)' : 'Notifications'}
            >
                {/* Removed numeric counter as per blueprint */}
            </button>
            
            <NotificationsDropdown
                isOpen={isDropdownOpen}
                onClose={handleCloseDropdown}
                hasError={hasError}
                onRetry={() => fetchCounts()}
                filter={filter}
                onFilterChange={handleFilterChange}
                totalCount={totalCount}
                unreadCount={unreadCount}
                onRefresh={() => fetchCounts()}
                isRefreshing={isLoading}
                currentTeamId={currentTeamId}
                refreshSignal={liveRefreshSignal}
            />
        </div>
    );
};

export default NotificationBadge; 
